import { UserProfession } from '~/enums/UserProfession';
import { Colors } from '~/themes/colors';
import { Column, Row, Text } from '~/commonComponents';
import { useMediaQuery } from '~/hooks';
import { ProfessionalNameRow } from '../shared/ProfessionalNameRow';
import { ActivityLevelInfo } from '../shared/ActivityLevelInfo';
import AttendanceBadgeInline from '../shared/AttendanceBadgeInline';
import { IProfessionalCardUser } from '../../interfaces';

interface IProfessionalCardDetailsProps {
  user: IProfessionalCardUser;
}

export const ProfessionalCardDetails = ({ user }: IProfessionalCardDetailsProps) => {
  const {
    id,
    fullname,
    profession,
    rating,
    isAvailable,
    activityLevel,
    yearsOfExperience,
    completedJobs,
    badges,
  } = user;
  const textColor = isAvailable ? Colors.neutral_500 : Colors.neutral_300;
  const hasBadges = badges && badges.length > 0;
  const [isSmallScreen] = useMediaQuery(['mlg']);

  return (
    <Column justifyContent="center" marginLeft={16} flex={1} minWidth={0}>
      {/* Row 1: Name + Rating + Profile */}
      <ProfessionalNameRow id={id} fullname={fullname} rating={rating} isAvailable={isAvailable} />

      {isSmallScreen ? (
        <>
          {/* Small screen — Line 1: Profession • Experience */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            <Text color={textColor}>{UserProfession.getName(profession || '')}</Text>
            {yearsOfExperience != null && (
              <Text color={textColor}>
                &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Text>
            )}
          </Row>

          {/* Small screen — Line 2: Completed Shifts • Activity Level */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            {completedJobs != null && (
              <Text color={textColor}>
                Completed Shifts:{' '}
                <Text bold color={textColor}>
                  {completedJobs}
                </Text>
              </Text>
            )}
            {completedJobs != null && activityLevel && (
              <Text color={textColor}>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</Text>
            )}
            {activityLevel && (
              <ActivityLevelInfo activityLevel={activityLevel} isAvailable={isAvailable} />
            )}
          </Row>

          {/* Small screen — Line 3: Attendance */}
          {hasBadges && (
            <Row alignItems="center" gap={4} style={{ whiteSpace: 'nowrap' }}>
              <Text color={textColor}>Attendance:</Text>
              <AttendanceBadgeInline badges={badges} />
            </Row>
          )}
        </>
      ) : (
        <>
          {/* Big screen — Line 1: Profession • Experience • Completed Shifts */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            <Text color={textColor}>{UserProfession.getName(profession || '')}</Text>
            {yearsOfExperience != null && (
              <Text color={textColor}>
                &nbsp;&nbsp;&bull;&nbsp;&nbsp;
                {yearsOfExperience} yr{yearsOfExperience !== 1 ? 's' : ''} Experience
              </Text>
            )}
            {completedJobs != null && (
              <Text color={textColor}>
                &nbsp;&nbsp;&bull;&nbsp;&nbsp; Completed Shifts:{' '}
                <Text bold color={textColor}>
                  {completedJobs}
                </Text>
              </Text>
            )}
          </Row>

          {/* Big screen — Line 2: Activity Level • Attendance */}
          <Row alignItems="center" style={{ whiteSpace: 'nowrap' }}>
            {activityLevel && (
              <ActivityLevelInfo activityLevel={activityLevel} isAvailable={isAvailable} />
            )}
            {hasBadges && activityLevel && (
              <Text color={textColor}>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</Text>
            )}
            {hasBadges && (
              <Row alignItems="center" gap={4}>
                <Text color={textColor}>Attendance:</Text>
                <AttendanceBadgeInline badges={badges} />
              </Row>
            )}
          </Row>
        </>
      )}
    </Column>
  );
};
